import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAddresses,
  addAddress,
  updateAddress,
  deleteAddress,
} from "../redux/actions/addressActions";
import * as other from "../components/other"; // your Modal
import axios from "axios";

const Address = () => {
  const dispatch = useDispatch();
  const { addresses, loading, error } = useSelector((state) => state.address);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState(getInitialForm());
  const [pinAdress, setPinAdress] = useState(null);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    dispatch(fetchAddresses());
  }, [dispatch]);

  useEffect(() => {
    let temp = { ...setFormData };
    temp.city = pinAdress?.Block;
    temp.state = pinAdress?.Circle;
    temp.country = pinAdress?.Country;
    setFormData((prev) => ({
      ...prev,
      ...temp,
    }));
  }, [pinAdress]);

  useEffect(() => {
    // Automatically set the only address as default if not already
    if (Array.isArray(addresses) && addresses.length === 1) {
      const addr = addresses[0];
      if (!addr.is_default) {
        const pk = addr.id ?? addr.address_id;
        dispatch(updateAddress(pk, { ...addr, is_default: true }));
      }
    }
  }, [addresses, dispatch]);

  const getAddressByPin = async (pincode) => {
    try {
      if (pincode?.length !== 6) {
        alert("Enter a 6-digit PIN code");

        // ✅ Properly reset address fields
        setFormData((prev) => ({
          ...prev,
          pin_code: pincode,
          city: "",
          state: "",
          country: "",
        }));

        setPinAdress({});
        return;
      }

      const response = await axios.get(
        `https://api.postalpincode.in/pincode/${pincode}`
      );

      if (response.status === 200 && response.data?.[0]?.Status === "Success") {
        const postOfficeData = response.data[0].PostOffice[0];

        setPinAdress(postOfficeData);

        // ✅ Optionally update form fields automatically
        setFormData((prev) => ({
          ...prev,
          pin_code: pincode,
          city: postOfficeData.District || "",
          state: postOfficeData.State || "",
          country: postOfficeData.Country || "",
        }));

        return postOfficeData;
      } else {
        setFormData((prev) => ({
          ...prev,
          pin_code: pincode,
          city: "",
          state: "",
          country: "",
        }));

        setPinAdress({});
        return null;
      }
    } catch (error) {
      return null;
    }
  };

  function getInitialForm() {
    return {
      full_name: "",
      mobile_number: "",
      pin_code: "",
      house_no: "",
      street: "",
      landmark: "",
      city: "",
      state: "",
      country: "",
      address_type: "HOME",
      is_default: false,
    };
  }

  const openAddModal = () => {
    setEditingId(null);
    setFormData(getInitialForm());
    setIsModalOpen(true);
  };

  const openEditModal = (address) => {
    const pk = address.id ?? address.address_id;
    setEditingId(pk);
    setFormData({ ...address });
    setIsModalOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = () => {
    const requiredFields = {
      full_name: "Full Name",
      mobile_number: "Mobile Number",
      pin_code: "Pin Code",
      house_no: "House No.",
      street: "Street",
      landmark: "Landmark",
      city: "City",
      state: "State",
      country: "Country",
      // address_type and is_default are not required for validation
    };

    const emptyFields = Object.keys(requiredFields).filter(
      (key) => !formData[key]?.toString().trim()
    );

    if (emptyFields.length > 0) {
      const missing = emptyFields.map((key) => requiredFields[key]).join(", ");
      alert(`Please fill in the following field(s): ${missing}`);
      return;
    }

    // If all required fields are filled, proceed
    if (editingId != null) {
      dispatch(updateAddress(editingId, formData));
    } else {
      dispatch(addAddress(formData));
    }

    setIsModalOpen(false);
  };

  const handleDelete = (address) => {
    const pk = address.id ?? address.address_id;
    dispatch(deleteAddress(pk));
  };

  return (
    <div className="max-w-6xl mx-auto min-h-screen w-full flex items-center justify-center pt-6">
      <div className=" min-h-[90vh] rounded-2xl w-full pt-6 flex flex-col items-center">
        {/* Header */}
        <div className="w-full px-4 flex justify-between items-center mb-10 max-w-8xl">
          <h2 className="text-2xl font-semibold">My Address</h2>
          <button
            className="px-4 py-2 bg-red-600 text-white text-sm rounded-xl hover:bg-red-700"
            onClick={openAddModal}
          >
            + Add Address
          </button>
        </div>

        {/* Address List */}
        <div className="w-full max-w-5xl p-6">
          {loading && <p>Loading...</p>}
          {!loading && error && <p className="text-red-500">{error}</p>}
          {!loading &&
            !error &&
            (Array.isArray(addresses) && addresses.length > 0 ? (
              addresses.map((address) => {
                const pk = address.id ?? address.address_id;
                return (
                  <div
                    key={pk}
                    className="border mb-4 rounded-[20px] p-6 bg-white shadow-md relative flex flex-col cursor-pointer 
                              gap-3 transform transition duration-300 ease-in-out hover:scale-95"
                  >
                    <div className="absolute top-3 right-3 flex gap-5">
                      <button
                        onClick={() => openEditModal(address)}
                        className="text-gray-700 hover:text-red-500"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDelete(address)}
                        className="text-red-500 hover:text-red-700"
                      >
                        🗑️
                      </button>
                    </div>
                    <div className="text-sm text-gray-800 space-y-1">
                      <p className="font-semibold text-base">
                        {address.full_name}
                      </p>
                      <p>
                        {address.house_no}, {address.street}, {address.landmark}
                      </p>
                      <p>
                        {address.city}, {address.state}, {address.country} -{" "}
                        {address.pin_code}
                      </p>
                      <p>Phone: {address.mobile_number}</p>
                      <p>Type: {address.address_type}</p>
                      <p className="text-xs text-gray-500">
                        Default: {address.is_default ? "Yes" : "No"}
                      </p>
                    </div>
                    <div className="mt-4 flex justify-end">
                      <button
                        type="button"
                        className="flex items-center gap-2 border border-neutral-800/50 text-red-600 text-xs font-semibold px-3 py-1.5 rounded-md hover:bg-red-100 disabled:opacity-50"
                        onClick={() =>
                          dispatch(
                            updateAddress(pk, { ...address, is_default: true })
                          )
                        }
                        disabled={address.is_default}
                      >
                        <input
                          type="checkbox"
                          checked={address.is_default}
                          readOnly
                          className="accent-red-500 w-4 h-4"
                        />
                        <label className="cursor-pointer select-none">
                          Set as Default
                        </label>
                      </button>
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-gray-500">No addresses found.</p>
            ))}
        </div>
      </div>

      {/* Add/Edit Modal */}
      <other.Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingId != null ? "Update Address" : "Add Address"}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Full Name */}
          <input
            type="text"
            name="full_name"
            placeholder="Full Name"
            value={formData.full_name}
            onChange={handleInputChange}
            className="w-full border border-gray-300 rounded-lg px-4 py-2 bg-white
                focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-200"
          />

          {/* Mobile Number */}
          <input
            type="text"
            name="mobile_number"
            placeholder="Mobile Number"
            maxLength={10}
            value={formData.mobile_number}
            onChange={handleInputChange}
            className="w-full border border-gray-300 rounded-lg px-4 py-2 bg-white
                focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-200"
          />

          {/* Pin Code */}
          <input
            type="text"
            name="pin_code"
            placeholder="Pin Code"
            value={formData.pin_code}
            onChange={handleInputChange}
            onBlur={(e) => getAddressByPin(e.target.value)} // 🔧 Corrected from 'on' to 'onBlur'
            className="w-full border border-gray-300 rounded-lg px-4 py-2 bg-white
      focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-200"
          />

          {/* House No */}
          <input
            type="text"
            name="house_no"
            placeholder="House No"
            value={formData.house_no}
            onChange={handleInputChange}
            className="w-full border border-gray-300 rounded-lg px-4 py-2 bg-white
                focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-200"
          />

          {/* Street */}
          <input
            type="text"
            name="street"
            placeholder="Street"
            value={formData.street}
            onChange={handleInputChange}
            className="w-full border border-gray-300 rounded-lg px-4 py-2 bg-white
                focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-200"
          />

          {/* Landmark */}
          <input
            type="text"
            name="landmark"
            placeholder="Landmark"
            value={formData.landmark}
            onChange={handleInputChange}
            className="w-full border border-gray-300 rounded-lg px-4 py-2 bg-white
                focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-200"
          />

          {/* City */}
          <input
            type="text"
            name="city"
            placeholder="City"
            value={formData.city}
            onChange={handleInputChange}
            className="w-full border border-gray-300 rounded-lg px-4 py-2 bg-white
                focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-200"
          />

          {/* State */}
          <input
            type="text"
            name="state"
            placeholder="State"
            value={formData.state}
            onChange={handleInputChange}
            className="w-full border border-gray-300 rounded-lg px-4 py-2 bg-white
                focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-200"
          />

          {/* Country */}
          <input
            type="text"
            name="country"
            placeholder="Country"
            value={formData.country}
            onChange={handleInputChange}
            className="w-full border border-gray-300 rounded-lg px-4 py-2 bg-white
                focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-200"
          />

          {/* Address Type */}
          <select
            name="address_type"
            value={formData.address_type}
            onChange={handleInputChange}
            className="w-full border border-gray-300 rounded-lg px-4 py-2 bg-white
                focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-200"
          >
            <option value="HOME">Home</option>
            <option value="WORK">Work</option>
          </select>
        </div>

        <button
          onClick={handleSubmit}
          className="mt-6 w-full py-3 shadow-sm btn theme-btn px-4"
        >
          {editingId != null ? "Update Address" : "Add Address"}
        </button>
      </other.Modal>
    </div>
  );
};

export default Address;
